import * as React from 'react';
import { X } from '../Icon/icons';
import type { BadgeTone } from '../Badge/Badge';
import { Icon } from '../Icon/Icon';
import { tag as tagCls, removeBtn as removeBtnCls } from './Tag.css';

export type TagSize = 'sm' | 'md';

export interface TagProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'onSelect'> {
  tone?: BadgeTone;
  size?: TagSize;
  /** Selected state flips subtle → solid. */
  selected?: boolean;
  /** Show a remove X. Also makes the chip focusable. */
  removable?: boolean;
  /** Remove-button callback. Backspace/Delete while focused also triggers. */
  onRemove?: () => void;
  /** Click anywhere on the chip body (ignores the X). Toggle behavior. */
  onToggle?: () => void;
  icon?: React.ReactNode;
  /** React 19 ref-as-prop. */
  ref?: React.Ref<HTMLSpanElement>;
}

/**
 * Tag — interactive chip used for filters, category labels, and selectable
 * tokens. Two interactive modes:
 *
 *   - `onToggle` — chip becomes a `role="button"` with `aria-pressed`; it
 *     is the focusable tab stop and Enter / Space activates. Selected
 *     state flips subtle → solid fill.
 *   - `removable` — adds a labelled X `<button>` which is the focusable
 *     tab stop. A *remove-only* chip (no `onToggle`) does NOT make the
 *     chip root a focusable tab stop — that would add an unlabelled,
 *     role-less stop in front of the X. Backspace / Delete still fire
 *     `onRemove` while the chip body has focus (e.g. when it's also a
 *     toggle, or focus is moved programmatically).
 *
 * Set both for a chip that toggles on click and removes via the X.
 */
export function Tag({
  tone = 'neutral',
  size = 'md',
  selected = false,
  removable = false,
  onRemove,
  onToggle,
  icon,
  children,
  onKeyDown,
  ref,
  className,
  ...rest
}: TagProps) {
  const isToggle = Boolean(onToggle);
  // Visual interactive treatment (focus ring + cursor) applies to any
  // chip the user can act on. The chip *root* is only a focusable tab
  // stop when it's a real toggle button — a remove-only chip lets its
  // labelled X button own the tab stop (L7).
  const interactive = isToggle || removable;

  const handleKeyDown: React.KeyboardEventHandler<HTMLSpanElement> = (e) => {
    if ((e.key === 'Backspace' || e.key === 'Delete') && removable) {
      e.preventDefault();
      onRemove?.();
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      if (onToggle) {
        e.preventDefault();
        onToggle();
      }
    }
    onKeyDown?.(e);
  };

  const cls = [tagCls({ tone, size, selected, interactive }), className].filter(Boolean).join(' ');

  return (
    <span
      ref={ref}
      className={cls}
      role={isToggle ? 'button' : undefined}
      tabIndex={isToggle ? 0 : undefined}
      aria-pressed={isToggle ? selected : undefined}
      onClick={onToggle}
      onKeyDown={handleKeyDown}
      {...rest}
    >
      {icon}
      <span>{children}</span>
      {removable && (
        <button
          type="button"
          aria-label="Remove"
          className={removeBtnCls}
          onClick={(e) => {
            e.stopPropagation();
            onRemove?.();
          }}
        >
          <Icon as={X} size={12} strokeWidth={2} />
        </button>
      )}
    </span>
  );
}
Tag.displayName = 'Tag';
